import { PHYSICS } from '../config';

/**
 * Fixed timestep game loop manager
 * Ensures deterministic physics at 60Hz regardless of frame rate
 */
export class FixedTimestep {
  private accumulator: number = 0;
  private lastTime: number = 0;
  private fixedDt: number;
  private maxAccumulator: number;

  // Callbacks
  private fixedUpdateFn: ((dt: number) => void) | null = null;
  private renderFn: ((alpha: number) => void) | null = null;

  constructor() {
    this.fixedDt = PHYSICS.FIXED_DT;
    this.maxAccumulator = this.fixedDt * 5; // Prevent spiral of death
  }

  /**
   * Set the fixed update callback (physics, game logic)
   */
  onFixedUpdate(fn: (dt: number) => void): void {
    this.fixedUpdateFn = fn;
  }

  /**
   * Set the render callback (interpolation)
   */
  onRender(fn: (alpha: number) => void): void {
    this.renderFn = fn;
  }

  /**
   * Call this from Phaser's update loop
   * @param time Current timestamp in ms
   * @param delta Frame delta in ms (unused, we use our own accumulator)
   */
  update(time: number, _delta: number): void {
    if (this.lastTime === 0) {
      this.lastTime = time;
      return;
    }

    let frameTime = time - this.lastTime;
    this.lastTime = time;

    // Clamp to prevent spiral of death
    if (frameTime > this.maxAccumulator) {
      frameTime = this.maxAccumulator;
    }

    this.accumulator += frameTime;

    // Run fixed updates
    while (this.accumulator >= this.fixedDt) {
      if (this.fixedUpdateFn) {
        this.fixedUpdateFn(this.fixedDt);
      }
      this.accumulator -= this.fixedDt;
    }

    // Render with interpolation alpha
    const alpha = this.accumulator / this.fixedDt;
    if (this.renderFn) {
      this.renderFn(alpha);
    }
  }

  /**
   * Reset the timestep (call on scene restart)
   */
  reset(): void {
    this.accumulator = 0;
    this.lastTime = 0;
  }
}
